import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { MessageCircle, X, ExternalLink } from 'lucide-react';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useChatUnreadChats } from '../hooks/useChatUnreadChats';
import TicketChatView from './TicketChatView';
import StateBlock from './ui/StateBlock';
import { getInitials, getAvatarColor } from '../hooks/useLiveChatNotifications';
import type { ConversationGroup } from '../types';

const ts = (v: string | null | undefined): number => { const n = v ? Date.parse(v) : NaN; return Number.isNaN(n) ? 0 : n; };

function BubbleChat({ ticketId, onBack }: { ticketId: string; onBack: () => void }) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({ queryKey: ['ticket-timeline', ticketId], queryFn: () => api.getTicketTimeline(ticketId) });
  const { data: ticketData } = useQuery({ queryKey: ['ticket', ticketId], queryFn: () => api.getTicket(ticketId) });
  const ticket = ticketData?.data;
  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-300/60">
        <button type="button" onClick={onBack} className="text-sm text-accent-text">{t('chatScreen.bubble.open')}</button>
        <span className="flex-1 truncate text-sm font-semibold text-surface-900">{ticket?.title ?? ''}</span>
        <Link to={`/discussions?ticket=${ticketId}`} className="p-1 text-surface-500 hover:text-accent-text" title={t('chatScreen.bubble.openFull')} aria-label={t('chatScreen.bubble.openFull')}>
          <ExternalLink size={16} />
        </Link>
      </div>
      {isLoading ? (
        <StateBlock kind="loading" rows={4} className="flex-1" />
      ) : (
        <TicketChatView
          key={ticketId}
          timeline={data?.data?.timeline ?? []}
          ticketId={ticketId}
          mentionScope="chat_group"
          taskType={ticket?.task_type}
          isConversation={ticket?.is_conversation === true}
          hasCustomer={(ticket?.extra_customers?.length ?? 0) > 0 || ticket?.organization_is_internal === false}
          className="flex-1 min-h-0"
        />
      )}
    </div>
  );
}

/**
 * D-326: the floating chat bubble on every page: a small button with the unread-chats counter that opens
 * the chats in a side window (no leaving the page). It sits ABOVE the "+" button, never over it.
 */
export default function ChatBubble() {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const unread = useChatUnreadChats();
  const [open, setOpen] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const staff = user?.role === 'admin' || user?.role === 'agent';
  const onChatPage = location.pathname.startsWith('/discussions');
  const { data } = useQuery({
    queryKey: ['conversations-grouped', 'bubble'],
    queryFn: () => api.getConversationsGrouped({ filter: 'discussion' }),
    enabled: staff && open,
    refetchInterval: open ? 15_000 : false,
  });
  if (!staff || onChatPage) return null;
  const groups: ConversationGroup[] = [...(data?.data ?? [])]
    .sort((a, b) => (b.total_unread > 0 ? 1 : 0) - (a.total_unread > 0 ? 1 : 0) || ts(b.last_activity_at) - ts(a.last_activity_at))
    .slice(0, 30);
  const latest = (g: ConversationGroup) => g.tickets.reduce((b, x) => (ts(x.last_comment_at) > ts(b.last_comment_at) ? x : b), g.tickets[0]);

  return (
    <>
      <button
        type="button"
        data-testid="chat-bubble"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={t('chatScreen.bubble.open')}
        className="fixed z-nav w-11 h-11 rounded-full bg-surface-0 border border-surface-300 shadow-float flex items-center justify-center text-accent-text"
        style={{ bottom: 'calc(var(--fab-bottom, 1.5rem) + var(--fab-size, 3rem) + 0.75rem)', insetInlineEnd: 'var(--fab-inset, 1rem)' }}
      >
        <MessageCircle size={20} aria-hidden="true" />
        {unread > 0 && (
          <span data-testid="chat-bubble-count" className="absolute -top-1 -end-1 min-w-5 h-5 px-1 rounded-full bg-solid-success text-white text-xs font-semibold tabular-nums flex items-center justify-center">{unread > 99 ? '99+' : unread}</span>
        )}
      </button>
      {open && (
        <aside
          role="dialog"
          aria-label={t('chatScreen.bubble.open')}
          data-testid="chat-bubble-panel"
          className="fixed z-nav top-16 bottom-0 end-0 w-full sm:w-96 bg-surface-0 border-s border-surface-300/60 shadow-elevated flex flex-col"
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-300/60">
            <MessageCircle size={18} className="text-accent-text" aria-hidden="true" />
            <span className="flex-1 font-semibold text-surface-900">{t('chatScreen.bubble.open')}</span>
            <Link to="/discussions" className="p-1 text-surface-500 hover:text-accent-text" title={t('chatScreen.bubble.openFull')} aria-label={t('chatScreen.bubble.openFull')}><ExternalLink size={16} /></Link>
            <button type="button" onClick={() => { setOpen(false); setTicketId(null); }} className="p-1 text-surface-500" aria-label={t('chatScreen.bubble.close')}><X size={18} /></button>
          </div>
          {ticketId ? (
            <BubbleChat ticketId={ticketId} onBack={() => setTicketId(null)} />
          ) : (
            <div className="flex-1 overflow-y-auto min-h-0">
              {groups.length === 0 && <StateBlock kind="empty" icon={MessageCircle} title={t('chatScreen.noChats')} description={null} />}
              {groups.map((g) => {
                const lt = latest(g);
                const name = lt?.task_type === 'discussion' ? (lt.title || g.display_name || '') : (g.display_name || g.customer_name || lt?.title || '');
                return (
                  <button key={g.group_key} type="button" onClick={() => lt && setTicketId(lt.id)} className="w-full flex items-center gap-3 px-3 py-2.5 text-start border-b border-surface-200/80 hover:bg-surface-50">
                    <span className={`w-10 h-10 rounded-full ${getAvatarColor(name)} text-white text-sm font-semibold flex items-center justify-center flex-shrink-0`}>{getInitials(name)}</span>
                    <span className={`flex-1 min-w-0 truncate text-sm ${g.total_unread > 0 ? 'font-semibold text-surface-900' : 'text-surface-800'}`}>{name}</span>
                    {g.total_unread > 0 && <span className="min-w-5 h-5 px-1 rounded-full bg-solid-success text-white text-xs tabular-nums flex items-center justify-center">{g.total_unread > 9 ? '9+' : g.total_unread}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </aside>
      )}
    </>
  );
}
